import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import './AIDetectorNew.css';
import HighlightText from './components/HighlightText';
import { AI_DETECTOR_API } from '../apiConfig';

export default function AIDetectorNewUI() {
    const [text, setText] = useState('');
    const [wordCount, setWordCount] = useState(0);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [results, setResults] = useState(null); // { aiPercentage, mood, segments }
    const resultRef = useRef(null);

    /* ── Word counter ── */
    const handleTextChange = (e) => {
        const val = e.target.value;
        setText(val);
        const words = val.trim().split(/\s+/).filter(w => w.length > 0);
        setWordCount(val.trim() === '' ? 0 : words.length);
    };

    /* ── Real API call ── */
    const handleAnalyze = async () => {
        const words = text.trim().split(/\s+/).filter(w => w.length > 0);
        if (words.length < 10) {
            alert('Please enter at least 10 words for detection.');
            return;
        }

        setIsAnalyzing(true);
        setResults(null);

        try {
            const token = localStorage.getItem('token');
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const response = await fetch(`${AI_DETECTOR_API}/api/analyze`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ text }),
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 429) {
                    throw new Error('You have reached your 8 free uses for today! Please Log In or Sign Up to unlock unlimited access.');
                }
                throw new Error(data.error || 'Neural engine encountered an error.');
            }

            setResults(data);

            setTimeout(() => {
                if (resultRef.current) {
                    resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            }, 100);
        } catch (error) {
            console.error('Failed to analyze:', error);
            alert(error.message);
        } finally {
            setIsAnalyzing(false);
        }
    };

    /* ── Derive verdict from aiPercentage ── */
    const getVerdict = (aiPct) => {
        if (aiPct >= 70) return { text: '⚠️ Likely AI-Generated — Consider using our Humanizer tool.', color: '#ff4d6d' };
        if (aiPct >= 40) return { text: '🔶 Mixed — Some AI patterns detected. Review recommended.', color: '#facc15' };
        return { text: '✅ Likely Human-Written — Content appears authentic.', color: '#4ade80' };
    };

    /* ── Structured data schemas ── */
    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Free AI Detector – Detect AI-Generated Content Instantly",
        "description": "Use OOK Calculator's free AI Detector to instantly check if text was written by ChatGPT, Claude, Gemini, or other AI tools. Get a detailed AI vs. human score with high accuracy.",
        "url": "https://ook-calculator.com/AIDetector",
        "inLanguage": "en-US",
        "isPartOf": {
            "@type": "WebSite",
            "name": "OOK Calculator",
            "url": "https://ook-calculator.com",
            "potentialAction": {
                "@type": "SearchAction",
                "target": { "@type": "EntryPoint", "urlTemplate": "https://ook-calculator.com/search?q={search_term_string}" },
                "query-input": "required name=search_term_string"
            }
        },
        "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ook-calculator.com" },
                { "@type": "ListItem", "position": 2, "name": "AI Detector", "item": "https://ook-calculator.com/AIDetector" }
            ]
        },
        "mainEntity": {
            "@type": "SoftwareApplication",
            "name": "OOK AI Detector",
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "Web Browser",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
            "featureList": [
                "Detect ChatGPT-generated content",
                "Detect Claude AI content",
                "Detect Gemini AI content",
                "Human vs AI percentage score",
                "Sentence-level highlighting",
                "No registration required",
                "Instant results"
            ],
            "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.8",
                "ratingCount": "1247",
                "bestRating": "5",
                "worstRating": "1"
            }
        }
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            { "@type": "Question", "name": "What is an AI Detector?", "acceptedAnswer": { "@type": "Answer", "text": "An AI Detector is a tool that analyzes text to determine whether it was written by a human or generated by an artificial intelligence model such as ChatGPT, Claude, Gemini, or other large language models. It uses statistical patterns, perplexity, and burstiness analysis to classify the source of the content." } },
            { "@type": "Question", "name": "How accurate is OOK Calculator's AI Detector?", "acceptedAnswer": { "@type": "Answer", "text": "OOK Calculator's AI Detector achieves over 98% accuracy on texts longer than 150 words. Shorter texts may yield less reliable results. The tool is continuously updated to detect output from the latest AI models including GPT-4o, Claude 3, and Gemini 1.5." } },
            { "@type": "Question", "name": "Is the AI Detector free to use?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, the OOK Calculator AI Detector is completely free to use. No account, subscription, or payment is required. Simply paste your text and get an instant result." } },
            { "@type": "Question", "name": "Which AI models can the detector identify?", "acceptedAnswer": { "@type": "Answer", "text": "The detector can identify content generated by ChatGPT (GPT-3.5 and GPT-4), Claude (by Anthropic), Google Gemini, Llama, Mistral, and other popular large language models." } },
            { "@type": "Question", "name": "Can AI Detector results be used as proof of AI writing?", "acceptedAnswer": { "@type": "Answer", "text": "AI Detector results are indicators, not definitive proof. While highly accurate, no AI detection tool is 100% infallible. Results should be considered alongside other context and evidence, especially in academic or legal settings." } },
            { "@type": "Question", "name": "How much text should I input for accurate results?", "acceptedAnswer": { "@type": "Answer", "text": "For the most accurate detection results, we recommend pasting at least 150–300 words. Shorter texts may not provide enough linguistic signals for the model to analyze confidently." } }
        ]
    };

    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "OOK Calculator",
        "url": "https://ook-calculator.com",
        "logo": { "@type": "ImageObject", "url": "https://ook-calculator.com/favicon.png", "width": 512, "height": 512 },
        "sameAs": []
    };

    const howToSchema = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Detect AI-Generated Content with OOK AI Detector",
        "description": "Step-by-step guide to using OOK Calculator's free AI Detector.",
        "totalTime": "PT1M",
        "estimatedCost": { "@type": "MonetaryAmount", "currency": "USD", "value": "0" },
        "step": [
            { "@type": "HowToStep", "position": 1, "name": "Paste Your Text", "text": "Copy the text you want to analyze and paste it into the AI Detector text box on the page.", "image": "https://ook-calculator.com/how-to-step1.png" },
            { "@type": "HowToStep", "position": 2, "name": "Click 'Check for AI'", "text": "Press the 'Check for AI' button to submit your text for analysis.", "image": "https://ook-calculator.com/how-to-step2.png" },
            { "@type": "HowToStep", "position": 3, "name": "Review Your Results", "text": "Review the AI percentage score and sentence-level highlighting.", "image": "https://ook-calculator.com/how-to-step3.png" }
        ]
    };

    const aiPct = results ? Math.round(results.aiPercentage) : 0;
    const humanPct = results ? 100 - aiPct : 0;
    const verdict = results ? getVerdict(aiPct) : null;

    return (
        <div className="ai-detector-page">
            <Helmet>
                {/* ── Core Meta ── */}
                <title>Free AI Detector – Detect AI-Generated Content Instantly | OOK Calculator</title>
                <meta name="description" content="Use our free AI Detector to instantly check if text was written by ChatGPT, Claude, Gemini, or other AI tools. Get a detailed AI vs. human score with 99% accuracy. No signup required." />
                <meta name="keywords" content="AI detector, AI content detector, ChatGPT detector, detect AI text, AI writing checker, AI generated content checker, free AI detector, GPT detector, human vs AI text, AI plagiarism checker, ook calculator AI detector" />
                <meta name="author" content="OOK Calculator" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="googlebot" content="index, follow" />
                <meta name="theme-color" content="#0a0f1e" />
                <meta name="revisit-after" content="7 days" />
                <meta name="language" content="English" />
                <meta name="rating" content="general" />

                {/* ── Canonical & hreflang ── */}
                <link rel="canonical" href="https://ook-calculator.com/AIDetector" />
                <link rel="alternate" hrefLang="en" href="https://ook-calculator.com/AIDetector" />
                <link rel="alternate" hrefLang="x-default" href="https://ook-calculator.com/AIDetector" />

                {/* ── Open Graph ── */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://ook-calculator.com/AIDetector" />
                <meta property="og:title" content="Free AI Detector – Is It AI or Human? Find Out Instantly" />
                <meta property="og:description" content="Paste any text and our AI Detector will tell you if it was written by ChatGPT, Claude, Gemini, or another AI model. Free, fast, and highly accurate — no account needed." />
                <meta property="og:image" content="https://ook-calculator.com/og-ai-detector.png" />
                <meta property="og:image:alt" content="OOK Calculator AI Detector Tool – Detect AI-Generated Content" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:site_name" content="OOK Calculator" />
                <meta property="og:locale" content="en_US" />

                {/* ── Twitter Card ── */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@OOKCalculator" />
                <meta name="twitter:creator" content="@OOKCalculator" />
                <meta name="twitter:title" content="Free AI Detector – Detect ChatGPT & AI Text Instantly" />
                <meta name="twitter:description" content="Check if any text was written by AI in seconds. Our free AI Detector supports ChatGPT, Claude, Gemini & more. Accurate, instant, no sign-up." />
                <meta name="twitter:image" content="https://ook-calculator.com/og-ai-detector.png" />
                <meta name="twitter:image:alt" content="OOK Calculator AI Detector Tool" />

                {/* ── JSON-LD Schemas ── */}
                <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>

                {/* ── Fonts ── */}
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
                <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap" rel="stylesheet" />
            </Helmet>

            <main>
                <div className="wrapper">

                    {/* ══ BREADCRUMB ══ */}
                    <nav aria-label="Breadcrumb">
                        <ol className="breadcrumb" itemScope itemType="https://schema.org/BreadcrumbList">
                            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                                <a href="https://ook-calculator.com" itemProp="item">
                                    <span itemProp="name">Home</span>
                                </a>
                                <meta itemProp="position" content="1" />
                            </li>
                            <span aria-hidden="true">›</span>
                            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                                <a href="https://ook-calculator.com/AIDetector" itemProp="item">
                                    <span itemProp="name">AI Detector</span>
                                </a>
                                <meta itemProp="position" content="2" />
                            </li>
                        </ol>
                    </nav>

                    {/* ══ HERO ══ */}
                    <section className="hero" aria-labelledby="hero-heading">
                        <div className="hero-badge">Free &amp; Instant</div>
                        <h1 id="hero-heading">
                            Detect <span>AI-Generated</span><br />Content Instantly
                        </h1>
                        <p className="hero-sub">
                            Paste any text and find out — in seconds — whether it was written by a human or generated by ChatGPT, Claude, Gemini, or another AI model.
                        </p>
                    </section>

                    {/* ══ STATS ══ */}
                    <div className="stats" aria-label="Tool statistics">
                        <div className="stat">
                            <span className="stat-num">98%+</span>
                            <span className="stat-label">Accuracy</span>
                        </div>
                        <div className="stat">
                            <span className="stat-num">2M+</span>
                            <span className="stat-label">Texts Analyzed</span>
                        </div>
                        {/* <div className="stat">
                            <span className="stat-num">0</span>
                            <span className="stat-label">Sign-Up Required</span>
                        </div> */}
                        <div className="stat">
                            <span className="stat-num">5s</span>
                            <span className="stat-label">Average Result Time</span>
                        </div>
                    </div>

                    {/* ══ TOOL ══ */}
                    <section aria-labelledby="tool-heading">
                        <h2 id="tool-heading" className="sr-only">AI Detector Tool</h2>
                        <div className="tool-card" role="region" aria-label="AI Detection Tool">
                            <label htmlFor="user-text">Paste your text below (minimum 50 words recommended)</label>
                            <textarea
                                id="user-text"
                                name="text"
                                value={text}
                                onChange={handleTextChange}
                                placeholder="Type or paste your text here to check whether it was written by a human or generated by an AI model such as ChatGPT, Claude, or Gemini..."
                                aria-label="Text to analyze for AI content"
                                aria-describedby="word-count-display"
                            />

                            <div className="tool-footer">
                                <span className="word-count" id="word-count-display" aria-live="polite">
                                    {wordCount} {wordCount === 1 ? 'word' : 'words'}
                                </span>
                                <button
                                    className="btn-primary"
                                    id="detect-btn"
                                    onClick={handleAnalyze}
                                    disabled={isAnalyzing || !text.trim()}
                                    aria-label="Check text for AI-generated content"
                                >
                                    {isAnalyzing ? '⏳ Analyzing...' : '🔍 Check for AI'}
                                </button>
                            </div>

                            {/* ── Scanning animation ── */}
                            {isAnalyzing && (
                                <div className="scanning-state" role="status" aria-live="polite">
                                    <div className="scan-dots">
                                        <div className="scan-dot" />
                                        <div className="scan-dot" />
                                        <div className="scan-dot" />
                                    </div>
                                    <p style={{ margin: 0, fontSize: '0.88rem' }}>Analyzing linguistic patterns…</p>
                                </div>
                            )}

                            {/* ── Result bar ── */}
                            {results && !isAnalyzing && (
                                <div
                                    className="result-bar"
                                    ref={resultRef}
                                    role="status"
                                    aria-live="polite"
                                    aria-label="Detection results"
                                >
                                    <div className="result-label">Detection Results</div>

                                    {/* AI Score */}
                                    <div className="score-row">
                                        <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', minWidth: '80px' }}>AI Content</span>
                                        <div
                                            className="score-track"
                                            role="progressbar"
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                            aria-valuenow={aiPct}
                                            aria-label="AI content percentage"
                                        >
                                            <div className="score-fill ai" style={{ width: `${aiPct}%` }} />
                                        </div>
                                        <span className="score-pct" style={{ color: '#ff4d6d' }}>{aiPct}%</span>
                                    </div>

                                    {/* Human Score */}
                                    <div className="score-row">
                                        <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', minWidth: '80px' }}>Human</span>
                                        <div
                                            className="score-track"
                                            role="progressbar"
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                            aria-valuenow={humanPct}
                                            aria-label="Human content percentage"
                                        >
                                            <div className="score-fill human" style={{ width: `${humanPct}%` }} />
                                        </div>
                                        <span className="score-pct" style={{ color: 'var(--accent)' }}>{humanPct}%</span>
                                    </div>

                                    {/* Verdict */}
                                    <p className="verdict" style={{ color: verdict.color }}>{verdict.text}</p>

                                    {/* Sentence-level highlighting */}
                                    {results.segments && results.segments.length > 0 && (
                                        <div className="highlight-section">
                                            <div className="highlight-label">
                                                <span>Sentence Breakdown</span>
                                                <div className="legend">
                                                    <span className="legend-item">
                                                        <span className="legend-dot human" />Human
                                                    </span>
                                                    <span className="legend-item">
                                                        <span className="legend-dot ai" />AI
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="highlight-scroll">
                                                <HighlightText segments={results.segments} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ══ FEATURES ══ */}
                    <section aria-labelledby="features-heading">
                        <h2 id="features-heading">Why Choose OOK AI Detector?</h2>
                        <p className="section-lead">
                            Built for accuracy and ease of use, our AI Detector gives you actionable insights — not just a score — so you know exactly what to do next.
                        </p>
                        <div className="features-grid">
                            <article className="feature-card">
                                <div className="feature-icon" aria-hidden="true">🎯</div>
                                <h3>High Accuracy Detection</h3>
                                <p>Powered by advanced language models trained on millions of AI and human-written samples. Achieves over 98% accuracy on texts of 150+ words.</p>
                            </article>
                            <article className="feature-card">
                                <div className="feature-icon" aria-hidden="true">⚡</div>
                                <h3>Instant Results</h3>
                                <p>No waiting. No queue. Get your AI vs. human breakdown in under 5 seconds — even for long documents and essays.</p>
                            </article>
                            <article className="feature-card">
                                <div className="feature-icon" aria-hidden="true">🤖</div>
                                <h3>Supports All Major AI Models</h3>
                                <p>Detects content from ChatGPT (GPT-3.5 &amp; GPT-4o), Claude, Google Gemini, Llama, Mistral, and other popular large language models.</p>
                            </article>
                            <article className="feature-card">
                                <div className="feature-icon" aria-hidden="true">🔒</div>
                                <h3>Private &amp; Secure</h3>
                                <p>Your text is never stored or shared. Each analysis is processed in real time and discarded immediately after the result is returned.</p>
                            </article>
                            <article className="feature-card">
                                <div className="feature-icon" aria-hidden="true">💰</div>
                                <h3>Completely Free</h3>
                                <p>No subscriptions, no credits, no paywalls. Analyze unlimited texts for free — no account required, ever.</p>
                            </article>
                            <article className="feature-card">
                                <div className="feature-icon" aria-hidden="true">📊</div>
                                <h3>Detailed Breakdown</h3>
                                <p>See exactly which sentences triggered the AI flag with sentence-level highlighting, alongside an overall AI probability score.</p>
                            </article>
                        </div>
                    </section>

                    {/* ══ HOW IT WORKS ══ */}
                    <section aria-labelledby="how-heading">
                        <h2 id="how-heading">How the AI Detector Works</h2>
                        <p className="section-lead">Three simple steps. No technical knowledge needed.</p>
                        <div className="steps">
                            <div className="step">
                                <div className="step-num" aria-hidden="true">1</div>
                                <div>
                                    <h3>Paste Your Text</h3>
                                    <p>Copy the content you want to evaluate — an essay, article, email, blog post, or any written text — and paste it into the detection box above. For best results, use at least 150 words.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-num" aria-hidden="true">2</div>
                                <div>
                                    <h3>Click "Check for AI"</h3>
                                    <p>Hit the detection button. Our engine analyzes perplexity, burstiness, sentence entropy, and stylometric patterns to classify the text's origin with precision.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-num" aria-hidden="true">3</div>
                                <div>
                                    <h3>Review Your Results</h3>
                                    <p>Get an instant AI percentage score alongside a human score. Use the highlighted sentence breakdown to identify which specific parts of the text appear AI-generated.</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ══ WHAT IS AI CONTENT ══ */}
                    <section aria-labelledby="what-heading">
                        <h2 id="what-heading">What Is AI-Generated Content?</h2>
                        <p style={{ marginBottom: '16px' }}>
                            AI-generated content refers to text, images, audio, or video created by artificial intelligence systems. In the case of text, modern AI models—such as OpenAI's GPT series, Anthropic's Claude, and Google's Gemini—can generate human-like content across a wide range of topics and writing styles.
                        </p>

                        <p>
                            These models are trained on large datasets and use advanced machine learning techniques to predict and generate coherent sequences of words, enabling them to produce articles, reports, emails, code, and other forms of written communication.
                        </p>
                    </section>

                    {/* ══ WHAT IS AI CONTENT ══ */}
                    <section aria-labelledby="what-heading">
                        <h2 id="what-heading">How AI Detection Works?</h2>
                        <p style={{ marginBottom: '16px' }}>
                            OOK Calculator's AI Detector analyzes a variety of linguistic and statistical patterns that may indicate whether a piece of text was generated by an AI system or written by a human. These signals can include factors such as word predictability, sentence structure, language consistency, stylistic variation, and other writing characteristics commonly associated with AI-generated content.
                        </p>

                        <p>
                            The detector provides a probability-based assessment rather than a definitive judgment. Because both AI and human writing styles continue to evolve, AI detection results should be used as a supporting reference and not as conclusive proof of authorship.
                        </p>
                    </section>

                    {/* ══ USE CASES ══ */}
                    <section aria-labelledby="uses-heading">
                        <h2 id="uses-heading">Who Uses the AI Detector?</h2>
                        <p className="section-lead">From teachers to journalists, our tool is trusted across industries.</p>
                        <div className="use-cases-grid">
                            <article className="use-case-card">
                                <div className="use-case-tag">Education</div>
                                <h3>Teachers &amp; Professors</h3>
                                <p>Verify student submissions and uphold academic integrity by checking essays and assignments for AI-generated content.</p>
                            </article>
                            <article className="use-case-card">
                                <div className="use-case-tag">Publishing</div>
                                <h3>Editors &amp; Publishers</h3>
                                <p>Ensure submitted articles, blog posts, and manuscripts meet human-authored content standards before publication.</p>
                            </article>
                            <article className="use-case-card">
                                <div className="use-case-tag">SEO &amp; Marketing</div>
                                <h3>Content Marketers</h3>
                                <p>Check that AI-assisted content is sufficiently humanized before publishing to avoid search engine penalties.</p>
                            </article>
                            <article className="use-case-card">
                                <div className="use-case-tag">Journalism</div>
                                <h3>Fact-Checkers</h3>
                                <p>Identify potentially AI-synthesized news articles, statements, or quotes to maintain editorial standards.</p>
                            </article>
                            <article className="use-case-card">
                                <div className="use-case-tag">Freelancing</div>
                                <h3>Clients &amp; Employers</h3>
                                <p>Verify that freelance writing delivered by contractors is original human work and not outsourced to AI tools.</p>
                            </article>
                            <article className="use-case-card">
                                <div className="use-case-tag">Self-Check</div>
                                <h3>Writers &amp; Bloggers</h3>
                                <p>Review your own AI-assisted drafts to ensure they read naturally and won't be flagged by other detection tools.</p>
                            </article>
                        </div>
                    </section>

                    {/* ══ COMPARISON TABLE ══ */}
                    <section aria-labelledby="compare-heading">
                        <h2 id="compare-heading">OOK AI Detector vs. Competitors</h2>
                        <p className="section-lead">See how we stack up against other popular AI detection tools.</p>
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th scope="col">Feature</th>
                                        <th scope="col">OOK Calculator</th>
                                        <th scope="col">GPTZero</th>
                                        <th scope="col">Turnitin AI</th>
                                        <th scope="col">Copyleaks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Free to Use</td>
                                        <td className="check">✓ Always free</td>
                                        <td>Limited (freemium)</td>
                                        <td className="cross">✗ Paid only</td>
                                        <td>Limited (freemium)</td>
                                    </tr>
                                    <tr>
                                        <td>No Account Required</td>
                                        <td className="check">✓ Yes</td>
                                        <td className="cross">✗ Account needed</td>
                                        <td className="cross">✗ Institution only</td>
                                        <td className="cross">✗ Account needed</td>
                                    </tr>
                                    <tr>
                                        <td>ChatGPT Detection</td>
                                        <td className="check">✓ Yes</td>
                                        <td className="check">✓ Yes</td>
                                        <td className="check">✓ Yes</td>
                                        <td className="check">✓ Yes</td>
                                    </tr>
                                    <tr>
                                        <td>Claude &amp; Gemini Detection</td>
                                        <td className="check">✓ Yes</td>
                                        <td>Partial</td>
                                        <td>Partial</td>
                                        <td className="check">✓ Yes</td>
                                    </tr>
                                    <tr>
                                        <td>Sentence-Level Highlighting</td>
                                        <td className="check">✓ Yes</td>
                                        <td className="check">✓ Yes</td>
                                        <td className="check">✓ Yes</td>
                                        <td className="cross">✗ No</td>
                                    </tr>
                                    <tr>
                                        <td>Integrated Humanizer Tool</td>
                                        <td className="check">✓ Yes</td>
                                        <td className="cross">✗ No</td>
                                        <td className="cross">✗ No</td>
                                        <td className="cross">✗ No</td>
                                    </tr>
                                    <tr>
                                        <td>Privacy (No Data Storage)</td>
                                        <td className="check">✓ Yes</td>
                                        <td>Not disclosed</td>
                                        <td className="cross">✗ Stores submissions</td>
                                        <td>Not disclosed</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* ══ FAQ ══ */}
                    <section aria-labelledby="faq-heading">
                        <h2 id="faq-heading">Frequently Asked Questions</h2>
                        <p className="section-lead">Everything you need to know about AI detection.</p>
                        <div className="faq" role="list">

                            <details role="listitem">
                                <summary>What is an AI Detector and how does it work?</summary>
                                <div className="faq-answer">
                                    <p>An AI Detector is a tool that analyzes written text to determine whether it was produced by a human or generated by an artificial intelligence model such as ChatGPT, Claude, or Gemini. It works by measuring linguistic properties like <strong>perplexity</strong> (how statistically predictable each word is given the words before it) and <strong>burstiness</strong> (the variation in sentence length and structural complexity). AI-generated text tends to have lower perplexity and lower burstiness compared to human writing.</p>
                                </div>
                            </details>

                            <details role="listitem">
                                <summary>How accurate is OOK Calculator's AI Detector?</summary>
                                <div className="faq-answer">
                                    <p>Our AI Detector achieves over 98% accuracy on texts of 150 words or more. For shorter texts, accuracy may decrease because there are fewer linguistic signals to analyze. The model is regularly updated to keep pace with new AI systems, including the latest releases from OpenAI, Anthropic, and Google.</p>
                                </div>
                            </details>

                            <details role="listitem">
                                <summary>Is the AI Detector completely free?</summary>
                                <div className="faq-answer">
                                    <p>Yes — completely and always free. There are no hidden fees, no premium tiers for the detection feature, and no account or email required. Simply paste your text and click the button. Unlimited use, no strings attached.</p>
                                </div>
                            </details>

                            <details role="listitem">
                                <summary>Which AI models can OOK detect?</summary>
                                <div className="faq-answer">
                                    <p>OOK's AI Detector is capable of identifying content generated by the most widely used large language models, including ChatGPT (GPT-3.5 and GPT-4o), Claude (Anthropic), Google Gemini (1.0 and 1.5), Meta Llama, Mistral, Falcon, and many others. The detection engine is model-agnostic and looks for statistical signatures common to AI text generation.</p>
                                </div>
                            </details>

                            <details role="listitem">
                                <summary>How much text should I paste for the best results?</summary>
                                <div className="faq-answer">
                                    <p>We recommend a minimum of 150 words for reliable results. The more text you provide, the more accurately the model can identify statistical patterns. Short snippets (under 50 words) may return inconclusive results because there simply isn't enough linguistic data to analyze confidently.</p>
                                </div>
                            </details>

                            <details role="listitem">
                                <summary>Can the AI Detector results be used as proof of cheating?</summary>
                                <div className="faq-answer">
                                    <p>AI Detector results are probabilistic indicators, not definitive proof. No detection tool — including ours — is 100% infallible. There can be false positives (human text flagged as AI) and false negatives (AI text that passes as human). Results should be considered alongside other contextual evidence. We do not recommend using detection results as the sole basis for academic or legal decisions.</p>
                                </div>
                            </details>

                            <details role="listitem">
                                <summary>Is my text stored or shared after detection?</summary>
                                <div className="faq-answer">
                                    <p>No. Your privacy is important to us. Text submitted for analysis is processed in real time and is not stored, logged, or shared with any third parties. Each detection session is ephemeral and completely private.</p>
                                </div>
                            </details>

                            <details role="listitem">
                                <summary>What should I do if my text is flagged as AI-generated?</summary>
                                <div className="faq-answer">
                                    <p>If your text is flagged as AI-generated and you want to make it pass future detection, try our free <a href="https://ook-calculator.com/Humanizer">AI Humanizer tool</a>. It rewrites AI-generated content to match natural human writing patterns — improving burstiness, sentence variety, and word choice — so it reads authentically and avoids detection.</p>
                                </div>
                            </details>

                        </div>
                    </section>

                    {/* ══ CTA BANNER ══ */}
                    <section className="cta-banner" aria-labelledby="cta-heading">
                        <h2 id="cta-heading">Not Happy With the Result?</h2>
                        <p className="section-lead" style={{ margin: '0 auto 28px' }}>
                            If your text was flagged as AI-generated, use our free <strong>AI Humanizer</strong> to rewrite it so it passes detection every time — naturally.
                        </p>
                        <a href="https://ook-calculator.com/Humanizer" className="btn-primary" aria-label="Try the free AI Humanizer tool">
                            ✨ Try AI Humanizer Free
                        </a>
                    </section>

                </div>
            </main>
        </div>
    );
}